import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateService } from "../../../../../../redux/actions/services.actions";

const toInt = v => parseInt(v, 10);
const toIntList = arr =>
  (Array.isArray(arr) ? arr : []).map(toInt).filter(Number.isFinite);

// Tracks the currently-dragging service id (unassigned chip OR assigned
// job block) and exposes a drop handler that re-assigns it via
// updateService.
//
// The /services PATCH endpoint REPLACES the `workers` array, so on each
// drop we compute the new array by:
//   - starting from the service's existing worker ids,
//   - removing the source worker (drag from worker row → other worker),
//   - adding the drop target worker (deduped).
//
// The reducer flips shouldRefetchServicesAfterAction = "refetch", which
// EmployeeSchedule.js listens to and re-pulls schedule + unassigned, so
// the moved job appears in the new row immediately.
const useDragAssign = () => {
  const dispatch = useDispatch();
  const { employees, unassignedServices } = useSelector(
    state => state.servicesReducer
  );
  const [drag, setDrag] = useState(null);
  // `drag` shape: { id, sourceWorkerId | null }

  const startDrag = useCallback(
    (id, sourceWorkerId = null) => setDrag({ id, sourceWorkerId }),
    []
  );
  const endDrag = useCallback(() => setDrag(null), []);

  const handleDrop = useCallback(
    targetWorkerId => {
      if (!drag) return;
      const { id, sourceWorkerId } = drag;
      const targetId = toInt(targetWorkerId);

      // No-op when re-dropping on the same worker — avoid a useless PATCH
      // that would still refetch the schedule.
      if (sourceWorkerId != null && toInt(sourceWorkerId) === targetId) {
        setDrag(null);
        return;
      }

      // Locate the service: assigned jobs live inside an employee bucket;
      // unassigned ones in the dedicated unassignedServices array.
      let service = unassignedServices.find(s => s.id === id);
      if (!service) {
        for (const w of employees || []) {
          const buckets = [
            w.acceptedServices,
            w.inProgressServices,
            w.pausedServices,
            w.finishedServices,
            w.canceledServices
          ];
          for (const b of buckets) {
            const found = Array.isArray(b) && b.find(s => s.id === id);
            if (found) {
              service = found;
              break;
            }
          }
          if (service) break;
        }
      }
      if (!service) {
        setDrag(null);
        return;
      }

      const existingIds = toIntList(service.workers?.map(w => w.id));
      const next = Array.from(
        new Set(
          existingIds
            .filter(wid => sourceWorkerId == null || wid !== toInt(sourceWorkerId))
            .concat(targetId)
        )
      );
      dispatch(updateService({ workers: next }, id));
      setDrag(null);
    },
    [drag, employees, unassignedServices, dispatch]
  );

  // Drop target for the "Unassigned" section: removes the source worker
  // from the service. If that empties the workers array entirely, the BE
  // surfaces the service via /services?statuses[]=unassigned, so the
  // refetch puts it back into the unassigned strip naturally. Dragging an
  // already-unassigned chip here is a no-op.
  const handleDropToUnassigned = useCallback(() => {
    if (!drag) return;
    const { id, sourceWorkerId } = drag;
    if (sourceWorkerId == null) {
      setDrag(null);
      return;
    }
    let service = null;
    for (const w of employees || []) {
      const buckets = [
        w.acceptedServices,
        w.inProgressServices,
        w.pausedServices,
        w.finishedServices,
        w.canceledServices
      ];
      for (const b of buckets) {
        const found = Array.isArray(b) && b.find(s => s.id === id);
        if (found) {
          service = found;
          break;
        }
      }
      if (service) break;
    }
    if (!service) {
      setDrag(null);
      return;
    }
    const existingIds = toIntList(service.workers?.map(w => w.id));
    const next = existingIds.filter(wid => wid !== toInt(sourceWorkerId));
    dispatch(updateService({ workers: next }, id));
    setDrag(null);
  }, [drag, employees, dispatch]);

  // True when drag originated on a worker row — used by the unassigned
  // section to highlight as a valid drop target only in that case.
  const draggingFromWorker = drag != null && drag.sourceWorkerId != null;

  return {
    draggingId: drag?.id ?? null,
    draggingFromWorker,
    startDrag,
    endDrag,
    handleDrop,
    handleDropToUnassigned
  };
};

export default useDragAssign;
